import { useEffect, useRef, useState } from "react";
import { MR_BOOKS, MR_BOOKS_PORTRAIT, type MagicView, type MagicBook } from "../catalogs/magic-books";
import { MR_IMG_W, MR_IMG_H, MR_CAULDRON } from "../lib/magic-room-geometry";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";
import { safeLocalStorage } from "../lib/safe-storage";
import magicTileCauldronUrl from "../assets/magic-tile-cauldron.webp";

// ROADMAP ITEM 7 — **THE MAGIC ROOM SCENE COMES OUT OF `App.tsx`.** (2026-09-04)
//
// Her standing law: *"refactor `src/App.tsx` into components OPPORTUNISTICALLY as areas are touched
// — no big-bang rewrite."*
//
// >>> **AND THIS WINDOW MEASURED WHAT *CHEAP* ACTUALLY MEANS HERE, HAVING GOT IT WRONG ONCE.** <<<
// The Dream and Palmistry rooms were tried first and reverted: their imports were all shared, which
// is the test the last two extractions passed on, and **eleven smoke units still went red** — because
// a ROOM is counted by this house's *every room does X* laws (one delivery block, one saved-title
// composition, one photo resize, one dictation hook), and moving one out of `App.tsx` moves it out of
// every one of those counters at once. **A leaf does not do that.** `MagicScene` is named by no
// fixture in the repo and delivers no reading, which is why it is the one taken.
//
// **BODIES UNCHANGED, COMMENTS AND ALL**, and `useElementSize` comes with it because `MagicScene` is
// its only caller — a hook left behind alone would be an export nothing reads (R38/R165).

// MR-L4 — THE ROOM FILLS THE PAGE. The background is FULL-BLEED (edge-to-edge of the content area, full
// viewport height, per the site's full-bleed law — no letterboxed 16:9 box). The painted cauldron and the
// floating books are anchored to the ART in the background image's coordinate space under cover-crop, so
// they track the room at every width (recomputed on resize). The Spell Book is biggest and nearest the
// cauldron; the others orbit it (MR-L3 hierarchy), never covering it. On narrow/portrait screens the art's
// cauldron crops off-frame, so the books take a portrait drift and a small floating Cauldron entry docks
// where the cauldron would be. First visit briefly glows the Spell Book + the cauldron.
// MR-L5/MR-L6: the shelf is DATA in src/catalogs/magic-books.ts, the art's measurements in src/lib/magic-room-geometry.ts.

export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, []);
  return { ref, size };
}

export function MagicScene({ onOpen }: { onOpen: (v: MagicView) => void }) {
  const reduced = usePrefersReducedMotion();
  const [firstVisit, setFirstVisit] = useState(false);
  const { ref, size } = useElementSize<HTMLDivElement>();
  useEffect(() => {
    // SAFE-STORAGE-1: through the one door. With no store there is no "first visit" memory, so
    // every arrival is a first one — which is the same answer the old catch gave, minus a throw.
    const store = safeLocalStorage();
    if (store && !store.getItem("mr-scene-visited")) {
      store.setItem("mr-scene-visited", "1");
      setFirstVisit(true);
      const t = window.setTimeout(() => setFirstVisit(false), 4200);
      return () => window.clearTimeout(t);
    }
  }, []);

  const { w, h } = size;
  // Cover-crop transform: the background covers the room box (like CSS background-size: cover), centred.
  const scale = w && h ? Math.max(w / MR_IMG_W, h / MR_IMG_H) : 0;
  const sw = MR_IMG_W * scale, sh = MR_IMG_H * scale;
  const offX = (w - sw) / 2, offY = (h - sh) / 2;
  const artX = (fx: number) => offX + fx * sw;
  const artY = (fy: number) => offY + fy * sh;

  // The cauldron hotspot in screen coords, and whether the painted cauldron is actually on-frame.
  const cRect = { left: artX(MR_CAULDRON.x0), top: artY(MR_CAULDRON.y0), width: (MR_CAULDRON.x1 - MR_CAULDRON.x0) * sw, height: (MR_CAULDRON.y1 - MR_CAULDRON.y0) * sh };
  const cCenterX = cRect.left + cRect.width / 2;
  const portrait = w > 0 && w <= 820;
  const cauldronOnFrame = !portrait && cCenterX > 0 && cCenterX < w && cRect.left > -cRect.width * 0.15 && (cRect.left + cRect.width) < w + cRect.width * 0.15;
  const measured = w > 0 && h > 0;

  // MR-L5 HARD RULE: no book ever crops. Clamp each book's square box fully inside the safe area — below
  // the top nav, right of the fixed sidebar (only present > 1050px), clear of the other edges. When space
  // is tight the clamp brings a book DOWN/IN and depth (z-index) handles any overlap, never a crop.
  const hasSidebar = w > 1050;
  const safe = { top: 84, bottom: 18, left: hasSidebar ? 266 : 16, right: 16 };
  const clamp = (v: number, lo: number, hi: number) => (hi < lo ? (lo + hi) / 2 : Math.min(Math.max(v, lo), hi));

  // M5 — PORTRAIT (phone) composition. A DETERMINISTIC grid over the room backdrop, so nothing can ever
  // overlap at any mobile width (the old fractional-absolute + vw-sized books overlapped because a book's
  // height did not track its fy*h position across aspect ratios). Small tool books up top, the Spellbook
  // large in the middle (still visibly the biggest), and the Cauldron as a big floating hearth at the
  // bottom (substantially larger than the old 30vw sticker). Every cell is a generous thumb target.
  if (portrait) {
    const byKey = (k: MagicView) => MR_BOOKS.find((b) => b.key === k)!;
    const tools: MagicView[] = ["keys", "moses", "petition", "herbs", "oils"];
    const tile = (b: MagicBook, cls: string) => (
      <button type="button" key={b.key} className={`mr-ptile ${cls}`} onClick={() => onOpen(b.key)} aria-label={`Open ${b.label}`}>
        <span className="mr-ptile__float"><img src={b.img} alt="" aria-hidden="true" /></span>
        <span className="mr-ptile__name">{b.label}</span>
      </button>
    );
    const spellbook = byKey("spellbook");
    return (
      <div ref={ref} className={`mr-room is-portrait${reduced ? " is-reduced" : ""}${firstVisit ? " is-first" : ""}`}>
        <div className="mr-backdrop" aria-hidden="true" />
        {/* M4-4 (owner's design ruling, 2026-07-25): the Conjured Hoodoo spellbook comes FIRST, at the
            top. The smaller books sit directly under it, and the cauldron under them. It used to run the
            other way round, so the first thing a member met on a phone was five small tool books. */}
        <div className="mr-portrait">
          {tile(spellbook, "mr-ptile--spellbook")}
          <div className="mr-portrait__tools">{tools.map((k) => tile(byKey(k), "mr-ptile--tool"))}</div>
          <button type="button" className="mr-portrait__cauldron" onClick={() => onOpen("cauldron")} aria-label="Open the Cauldron">
            <span className="mr-portrait__cauldron-glow" aria-hidden="true" />
            <img src={magicTileCauldronUrl} alt="" aria-hidden="true" />
            <span className="mr-ptile__name">The Cauldron</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={`mr-room${reduced ? " is-reduced" : ""}${firstVisit ? " is-first" : ""}${portrait ? " is-portrait" : ""}`}>
      <div className="mr-backdrop" aria-hidden="true" />
      {measured && MR_BOOKS.map((b) => {
        if (portrait) {
          const p = MR_BOOKS_PORTRAIT[b.key];
          return (
            <button type="button" key={b.key} className={`mr-book ${b.cls}`} style={{ left: p.fx * w, top: p.fy * h, zIndex: b.z }} onClick={() => onOpen(b.key)} aria-label={`Open ${b.label}`}>
              <span className="mr-book__float"><img src={b.img} alt={b.label} /><span className="mr-book__name">{b.label}</span></span>
            </button>
          );
        }
        const sizePx = (b.vh / 100) * h;
        const half = sizePx / 2;
        const left = clamp(artX(b.fx), safe.left + half, w - safe.right - half);
        const top = clamp(artY(b.fy), safe.top + half, h - safe.bottom - half);
        return (
          <button type="button" key={b.key} className={`mr-book ${b.cls}`} style={{ left, top, width: sizePx, zIndex: b.z }} onClick={() => onOpen(b.key)} aria-label={`Open ${b.label}`}>
            <span className="mr-book__float">
              <img src={b.img} alt={b.label} />
              <span className="mr-book__name">{b.label}</span>
            </span>
          </button>
        );
      })}
      {measured && cauldronOnFrame ? (
        // Painted cauldron is on-frame: an invisible hotspot locked to the art, with an ember glow on hover.
        <button type="button" className="mr-cauldron" style={{ left: cRect.left, top: cRect.top, width: cRect.width, height: cRect.height }} onClick={() => onOpen("cauldron")} aria-label="Open the Cauldron">
          <span className="mr-cauldron__glow" aria-hidden="true" />
          <span className="mr-book__name mr-cauldron__name">The Cauldron</span>
        </button>
      ) : measured ? (
        // Cauldron cropped off-frame (portrait/narrow): a small floating Cauldron entry docks near it.
        <button type="button" className="mr-cauldron-dock" onClick={() => onOpen("cauldron")} aria-label="Open the Cauldron">
          <img src={magicTileCauldronUrl} alt="" aria-hidden="true" />
          <span className="mr-cauldron-dock__name">The Cauldron</span>
        </button>
      ) : null}
    </div>
  );
}
